/**
 * Consistent HE / Subcontractor PDF download names:
 * `<Type>_<Vendor>_<Date>_<Ticket>.pdf`
 */

type HePdfFilenameField = {
  field: string;
  path?: string;
  value?: string | null;
};

function sanitizePdfFilenamePart(value: string, fallback: string): string {
  const cleaned = value
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '_')
    .replace(/_+/g, '_')
    .replace(/^_+|_+$/g, '');
  return cleaned || fallback;
}

function extractHePdfTypeLabel(fields: HePdfFilenameField[]): string {
  const raw = fields.find(
    (field) => field.field === 'SubcontractorOrHERep' || (field.path || '').includes('SubcontractorOrHERep'),
  )?.value;
  const trimmed = String(raw || '').trim();
  if (/subcontractor/i.test(trimmed)) return 'Subcontractor';
  return 'Hired_Equipment';
}

function extractHePdfVendorName(fields: HePdfFilenameField[]): string {
  const setup = fields
    .find((field) => field.field === 'SubcontractorNameCom' && !(field.path || '').includes('WorkCompleted'))
    ?.value?.trim();
  if (setup) return setup;
  return fields.find((field) => field.field === 'SubcontractorNameCom')?.value?.trim() || '';
}

/** Calendar day of a work-date value: `YYYY-MM-DD…` is read as written, never shifted. */
function workDayYmd(raw: string): string {
  const value = raw.trim();
  const isoDay = value.match(/^(\d{4}-\d{2}-\d{2})/);
  if (isoDay) return isoDay[1];
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getFullYear();
  const month = String(parsed.getMonth() + 1).padStart(2, '0');
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * V2-052: first and last work date across the lines (line dates first, else the setup date).
 * Empty strings when the form has no date.
 */
export function extractHeWorkDateRange(fields: HePdfFilenameField[]): { from: string; to: string } {
  const lineDates = fields
    .filter((field) => field.field === 'ActualStartTime' && (field.path || '').includes('WorkCompleted'))
    .map((field) => workDayYmd(String(field.value || '')))
    .filter(Boolean);
  const dates = lineDates.length
    ? lineDates
    : fields
        .filter((field) => field.field === 'ActualStartTime')
        .map((field) => workDayYmd(String(field.value || '')))
        .filter(Boolean);
  if (dates.length === 0) return { from: '', to: '' };
  const sorted = [...dates].sort();
  return { from: sorted[0], to: sorted[sorted.length - 1] };
}

/** "2026-10-05" for one day, "2026-10-05 – 2026-10-11" for a range (V2-052, V2-073). */
export function formatHeWorkDateRange(range: { from: string; to: string }): string {
  if (!range.from) return '';
  return range.from === range.to ? range.from : `${range.from} – ${range.to}`;
}

/**
 * The submission's ticket number (e.g. HE1626-00004): the HiredEquipmentTicket field, else the
 * ticket in the form name. Empty when there is none yet.
 */
export function extractHeTicketNumber(form: { name?: string | null; fields: HePdfFilenameField[] }): string {
  const stored = form.fields.find((field) => field.field === 'HiredEquipmentTicket')?.value?.trim();
  if (stored) return stored;
  return form.name?.match(/\bHE\d+-\d+\b/)?.[0] ?? '';
}

/**
 * `<Type>_<Vendor>_<Date or first_last date>_<Ticket>.pdf` (V2-052). The ticket number replaces the
 * internal form id; a submission without one yet (the submit preview) has no last part.
 */
export function buildHiredEquipmentPdfFilename(form: {
  id: string | number;
  name?: string | null;
  fields: HePdfFilenameField[];
}): string {
  const type = extractHePdfTypeLabel(form.fields);
  const vendor = sanitizePdfFilenamePart(extractHePdfVendorName(form.fields), 'Vendor');
  const range = extractHeWorkDateRange(form.fields);
  const date = !range.from
    ? new Date().toISOString().slice(0, 10)
    : range.from === range.to
      ? range.from
      : `${range.from}_${range.to}`;
  const ticket = sanitizePdfFilenamePart(extractHeTicketNumber(form), '');
  return ticket ? `${type}_${vendor}_${date}_${ticket}.pdf` : `${type}_${vendor}_${date}.pdf`;
}
